import { RefereeEligibilities } from '../../components/referee-eligibilities';
import { RefereeStatusBadge } from '../../components/referee-status';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { Pagination } from '~/components/Pagination/Pagination';
import { useAuthentication } from '~/hooks/useAuthentication';
import { useFilterForm } from '~/hooks/useFilterForm';
import { useRefereeListQuery } from '../../queries';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { querySchema } from '../query-schema';
import { Fragment } from 'react/jsx-runtime';
import { Judge } from '~/apis/ssf';

function Row({ referee }: { referee: Judge }) {
  const { can } = useAuthentication();

  return (
    <Table.Row>
      <Table.Col>
        <Link to={`${referee.id}`}>
          {referee.person.first_name} {referee.person.last_name}
        </Link>
        <div style={{ fontSize: 'var(--size-3\\.5)' }}>
          <RefereeEligibilities referee={referee} />
        </div>
      </Table.Col>
      <Table.Col>
        <Link to={`/clubs/${referee.club.id}`}>{referee.club.name}</Link>
        <div style={{ fontSize: 'var(--size-3\\.5)' }}>{referee.club.district.name}</div>
      </Table.Col>
      <Table.Col>{referee.person.license_id}</Table.Col>
      <Table.Col>{referee.year}</Table.Col>
      {can('list-referee-admin') && (
        <Table.Col>
          <RefereeStatusBadge referee={referee} />
        </Table.Col>
      )}
      <Table.Col style={{ fontSize: 'var(--size-3\\.5)' }}>
        <div>{`Primära: ${referee.duty_summary?.primary ?? '-'}`}</div>
        <div>{`Sekundära: ${referee.duty_summary?.secondary ?? '-'}`}</div>
      </Table.Col>
      {can('list-referee-admin') && (
        <Table.Col align="right">
          <Table.Context
            items={[
              <ContextMenu.Item
                label="Sök licens för nästa år"
                key="search-license"
                to={`/referees/${referee.id}/licenses/create`}
              />,
              <ContextMenu.Item label="Ändra status" key="change-status" to={`/referees/${referee.id}/edit`} />,
            ]}
          />
        </Table.Col>
      )}
    </Table.Row>
  );
}

export function RefereeListTable() {
  const { values } = useFilterForm(querySchema.parse);
  const { can } = useAuthentication();

  const { data, isLoading } = useRefereeListQuery({
    status: values.state === 'ACTIVE' ? ['active' as const, 'warned' as const] : undefined,
    level: values.level !== 'ALL' ? [values.level] : undefined,
    ordering: 'person__first_name,person__last_name',
    person__club__district: values.district_id,
    page_size: values.pageSize,
    page: values.pageIndex,
    search: values.search,
    club: values.club_id,
  });

  return (
    <Fragment>
      <Table bg="even">
        <Table.Head>
          <Table.Row>
            <Table.Col as="th">Namn</Table.Col>
            <Table.Col as="th">Förening</Table.Col>
            <Table.Col as="th">Licensnummer</Table.Col>
            <Table.Col as="th">Licensår</Table.Col>
            {can('list-referee-admin') && <Table.Col as="th">Status</Table.Col>}
            <Table.Col as="th" tooltip="Baserat på senaste året med licens">
              Antal uppdrag
            </Table.Col>
            {can('list-referee-admin') && <Table.Col as="th" />}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading ? (
            <Table.Loading cols={can('list-referee-admin') ? 7 : 5} />
          ) : (
            data?.results.map(referee => <Row referee={referee} key={referee.id} />)
          )}
        </Table.Body>
      </Table>

      <Pagination count={data?.count} />
    </Fragment>
  );
}
